/**
 * Менюто. Един източник за навигацията, вместо повторен списък в шаблона.
 *
 * `children` се разгъват в лявото меню („Работа“ → „Служители“). `tabs` са
 * страниците на един раздел („Бригади“, „Умения“…): те не са в лявото меню, а
 * хоризонтално в горната лента; разделът в лявото меню води към първата от тях.
 */
import type { AccessLevel, Section } from '@/types/api'

export interface NavigationItem {
  /** Име на маршрут; за група или раздел със страници — само ключ. */
  name: string
  label: string
  icon: string
  children?: NavigationItem[]
  tabs?: NavigationItem[]
  /** Най-долу в лявото меню — за неща, които се пипат рядко. */
  bottom?: boolean
  /** Разделът на правата; без него точката се води по децата си. */
  section?: Section
  /** Нужното ниво — „edit“ за попълването на наряд. */
  need?: AccessLevel
  /** Само за администратор (Потребители). */
  adminOnly?: boolean
}

export const mainNavigation: NavigationItem[] = [
  { name: 'dashboard', label: 'Табло', icon: 'pi pi-home', section: 'finance' },
  { name: 'projects', label: 'Обекти', icon: 'pi pi-building', section: 'projects' },
  {
    name: 'finance-group', label: 'Финанси', icon: 'pi pi-wallet',
    children: [
      {
        name: 'naryads-group', label: 'Наряди', icon: 'pi pi-file-edit', section: 'naryads',
        tabs: [
          { name: 'naryads', label: 'Наряди', icon: 'pi pi-file-edit' },
          { name: 'advances', label: 'Аванси', icon: 'pi pi-arrow-circle-down' },
        ],
      },
      { name: 'ledger', label: 'Каса', icon: 'pi pi-wallet', section: 'finance' },
      { name: 'payments', label: 'Плащания', icon: 'pi pi-building-columns', section: 'finance' },
      { name: 'loans', label: 'Заеми', icon: 'pi pi-money-bill', section: 'finance' },
      {
        name: 'invoices-group', label: 'Фактури', icon: 'pi pi-receipt', section: 'finance',
        tabs: [
          { name: 'invoices', label: 'Фактури', icon: 'pi pi-receipt' },
          { name: 'invoices-cancelled', label: 'Анулирани', icon: 'pi pi-trash' },
        ],
      },
      { name: 'payment-run', label: 'За плащане', icon: 'pi pi-credit-card', section: 'finance' },
      { name: 'vat', label: 'ДДС', icon: 'pi pi-percentage', section: 'vat' },
    ],
  },
  {
    name: 'work-group', label: 'Работа', icon: 'pi pi-users',
    children: [
      {
        name: 'employees-group', label: 'Служители', icon: 'pi pi-id-card', section: 'work',
        tabs: [
          { name: 'employees', label: 'Служители', icon: 'pi pi-id-card' },
          { name: 'employee-work-schedule', label: 'График на служителите', icon: 'pi pi-calendar' },
          { name: 'crews', label: 'Бригади', icon: 'pi pi-sitemap' },
          { name: 'crew-schedule', label: 'График на бригадите', icon: 'pi pi-calendar' },
          { name: 'skills', label: 'Умения', icon: 'pi pi-star' },
        ],
      },
      {
        name: 'counterparties-group', label: 'Контрагенти', icon: 'pi pi-users', section: 'work',
        tabs: [
          { name: 'counterparties', label: 'Контрагенти', icon: 'pi pi-users' },
          { name: 'occupancy', label: 'Заетост', icon: 'pi pi-calendar' },
          { name: 'curation', label: 'Свързване на имена', icon: 'pi pi-link' },
        ],
      },
      { name: 'business-cards', label: 'Визитки', icon: 'pi pi-address-book', section: 'work' },
      { name: 'work-schedule', label: 'График на работата', icon: 'pi pi-calendar', section: 'work' },
      { name: 'site-reports', label: 'Отчети', icon: 'pi pi-phone', section: 'work' },
    ],
  },
  { name: 'contracts', label: 'Работи', icon: 'pi pi-wrench', section: 'contracts' },
  {
    name: 'scanning', label: 'Сканиране', icon: 'pi pi-camera', section: 'scanning',
    children: [
      { name: 'scan-documents', label: 'Сканиране', icon: 'pi pi-upload' },
      { name: 'scan-approvals', label: 'За одобрение', icon: 'pi pi-check-square' },
      { name: 'scanned-files', label: 'Сканирани', icon: 'pi pi-images' },
      { name: 'all-files', label: 'Всички файлове', icon: 'pi pi-folder-open' },
    ],
  },
  { name: 'materials', label: 'Материали', icon: 'pi pi-box', section: 'materials' },
  {
    name: 'settings', label: 'Настройки', icon: 'pi pi-cog', bottom: true,
    tabs: [
      { name: 'companies', label: 'Дружества', icon: 'pi pi-briefcase', section: 'settings' },
      { name: 'users', label: 'Потребители', icon: 'pi pi-user-edit', adminOnly: true },
    ],
  },
]

/**
 * Менюто на строителния техник: попълване, нарядите по неговите обекти и
 * материалите им. Офисните екрани не са за него, дори да ги отвори по адрес.
 */
export const technicianNavigation: NavigationItem[] = [
  { name: 'intake', label: 'Попълване', icon: 'pi pi-pencil', section: 'naryad_intake', need: 'edit' },
  { name: 'site-naryads', label: 'Наряди', icon: 'pi pi-file-edit', section: 'naryads' },
  { name: 'materials', label: 'Материали', icon: 'pi pi-box', section: 'materials' },
]

/** Каквото менюто иска да знае за потребителя. */
export interface NavigationAccess {
  isAdmin: boolean
  restricted: boolean
  can: (section: Section, need?: AccessLevel) => boolean
}

function allowedItem(item: NavigationItem, access: NavigationAccess): boolean {
  if (item.adminOnly) return access.isAdmin
  return item.section === undefined || access.can(item.section, item.need ?? 'view')
}

/** Менюто без недостъпното; група или раздел без нищо в тях изчезва. */
export function visibleNavigation(access: NavigationAccess): NavigationItem[] {
  const prune = (items: NavigationItem[]): NavigationItem[] => items.flatMap((item) => {
    if (!allowedItem(item, access)) return []
    const children = item.children && prune(item.children)
    const tabs = item.tabs && prune(item.tabs)
    if ((children && !children.length) || (tabs && !tabs.length)) return []
    return [{ ...item, ...(children ? { children } : {}), ...(tabs ? { tabs } : {}) }]
  })
  if (access.restricted) return prune(technicianNavigation)
  const items = prune(mainNavigation)
  // Офис служител, който и попълва — попълването е отделна точка горе.
  if (!access.isAdmin && access.can('naryad_intake', 'edit')) items.unshift(technicianNavigation[0])
  return items
}

/** Разделът на правата за всеки маршрут. Без запис — свободен за влезлия. */
const ROUTE_SECTION: Record<string, [Section, AccessLevel] | 'admin'> = {
  dashboard: ['finance', 'view'], reports: ['finance', 'view'],
  projects: ['projects', 'view'], project: ['projects', 'view'], 'project-new': ['projects', 'edit'],
  'project-act-dates': ['projects', 'view'], 'project-budget': ['projects', 'view'],
  'project-expenses': ['projects', 'view'],
  naryads: ['naryads', 'view'], naryad: ['naryads', 'view'], 'intake-review': ['naryads', 'view'],
  'naryad-payment-new': ['naryads', 'edit'], 'naryad-payment-draft': ['naryads', 'edit'],
  'naryad-entry': ['naryads', 'view'], 'project-naryadi': ['naryads', 'view'],
  'work-orders': ['naryads', 'view'], 'work-order-new': ['naryads', 'edit'], 'work-order': ['naryads', 'view'],
  'site-naryads': ['naryads', 'view'], 'site-naryad': ['naryads', 'view'],
  intake: ['naryad_intake', 'edit'],
  ledger: ['finance', 'view'], 'transaction-new': ['finance', 'edit'], transaction: ['finance', 'view'],
  'cash-order': ['finance', 'view'], 'project-ledger': ['finance', 'view'], payments: ['finance', 'view'],
  loans: ['finance', 'view'], loan: ['finance', 'view'], advances: ['naryads', 'view'], invoices: ['finance', 'view'],
  'invoices-cancelled': ['finance', 'view'],
  invoice: ['finance', 'view'], 'payment-run': ['finance', 'view'], vat: ['vat', 'view'],
  'employee-work-schedule': ['work', 'view'], employees: ['work', 'view'], employee: ['work', 'view'], crews: ['work', 'view'], skills: ['work', 'view'],
  'crew-schedule': ['work', 'view'], counterparties: ['work', 'view'], counterparty: ['work', 'view'],
  occupancy: ['work', 'view'], curation: ['work', 'view'], 'business-cards': ['work', 'view'],
  'work-schedule': ['work', 'view'], 'site-reports': ['work', 'view'], 'site-report': ['work', 'view'],
  contracts: ['contracts', 'view'], contract: ['contracts', 'view'],
  'scan-documents': ['scanning', 'view'], 'scan-approvals': ['scanning', 'view'],
  'scan-review': ['scanning', 'view'], 'scanned-files': ['scanning', 'view'], 'all-files': ['scanning', 'view'],
  materials: ['materials', 'view'],
  companies: ['settings', 'view'], users: 'admin',
}

/** Екраните на техника — другите са затворени за него, дори с право. */
const TECHNICIAN_ROUTES = new Set(['intake', 'site-naryads', 'site-naryad', 'materials', 'not-found'])

export function routeAllowed(routeName: unknown, access: NavigationAccess): boolean {
  const name = String(routeName ?? '')
  if (access.isAdmin) return true
  if (access.restricted && !TECHNICIAN_ROUTES.has(name)) return false
  const rule = ROUTE_SECTION[name]
  if (rule === undefined) return true
  if (rule === 'admin') return false
  return access.can(rule[0], rule[1])
}

/** Първият разрешен екран — началото след вход и пътят при затворен адрес. */
export function homeRoute(access: NavigationAccess): string | null {
  const first = (items: NavigationItem[]): string | null => {
    for (const item of items) {
      if (item.children) {
        const inner = first(item.children)
        if (inner) return inner
      } else return item.tabs ? item.tabs[0].name : item.name
    }
    return null
  }
  return first(visibleNavigation(access))
}

/**
 * Подстраниците подсветват своя раздел: редакторът на наряд стои под „Наряди“,
 * фактурата — под „Фактури“, отчетът — под „Отчети“.
 */
const SECTION_OF: Record<string, string> = {
  'scan-documents': 'scan-documents',
  'scan-approvals': 'scan-approvals',
  'scan-review': 'scan-approvals',
  'scanned-files': 'scanned-files',
  'all-files': 'all-files',
  dashboard: 'dashboard',
  naryads: 'naryads',
  naryad: 'naryads',
  'naryad-entry': 'naryads',
  'naryad-payment-new': 'naryads',
  'naryad-payment-draft': 'naryads',
  'work-orders': 'naryads',
  'work-order-new': 'naryads',
  'work-order': 'naryads',
  advances: 'advances',
  'payment-run': 'payment-run',
  loans: 'loans',
  loan: 'loans',
  ledger: 'ledger',
  'transaction-new': 'ledger',
  transaction: 'ledger',
  invoices: 'invoices',
  invoice: 'invoices',
  'invoices-cancelled': 'invoices-cancelled',
  'work-schedule': 'work-schedule',
  'site-reports': 'site-reports',
  'site-report': 'site-reports',
  projects: 'projects',
  counterparties: 'counterparties',
  counterparty: 'counterparties',
  'employee-work-schedule': 'employee-work-schedule',
  employees: 'employees',
  employee: 'employees',
  crews: 'crews',
  'crew-schedule': 'crew-schedule',
  skills: 'skills',
  occupancy: 'occupancy',
  curation: 'curation',
  'business-cards': 'business-cards',
  reports: 'reports',
  companies: 'companies',
  users: 'users',
  intake: 'intake',
  'site-naryads': 'site-naryads',
  'site-naryad': 'site-naryads',
}

export function sectionOf(routeName: unknown): string | undefined {
  return SECTION_OF[String(routeName ?? '')]
}

/** Дали точката от менюто или нещо под нея е отвореният раздел. */
export function contains(item: NavigationItem, section: string | undefined): boolean {
  if (section === undefined) return false
  return item.name === section
    || [...(item.children ?? []), ...(item.tabs ?? [])].some(child => contains(child, section))
}

/** Разделът, в който е отворената страница, ако има страници — за горната лента. */
export function pageTabsOf(
  section: string | undefined,
  items: NavigationItem[] = mainNavigation,
): NavigationItem | undefined {
  const find = (items: NavigationItem[]): NavigationItem | undefined => {
    for (const item of items) {
      if (item.tabs && contains(item, section)) return item
      const inner = find(item.children ?? [])
      if (inner) return inner
    }
    return undefined
  }
  return find(items)
}
